import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role hierarchy
const ROLES = {
    DIRECTEUR: { level: 100, label: "Directeur" },
    DIRECTEUR_ADJOINT: { level: 80, label: "Directeur Adjoint" },
    CHEF_CUISINE: { level: 50, label: "Chef Cuisine" },
    CUISINIER: { level: 20, label: "Cuisinier" }
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState({});

    const api = useCallback(() => {
        const instance = axios.create({
            baseURL: API_URL,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        return instance;
    }, [token]);

    useEffect(() => {
        const verifyToken = async () => {
            if (token) {
                try {
                    const response = await api().get('/auth/me');
                    setUser(response.data);
                    setPermissions(response.data.permissions || {});
                } catch (error) {
                    console.error('Token verification failed:', error);
                    logout();
                }
            }
            setLoading(false);
        };
        verifyToken();
    }, [token, api]);

    const login = async (identifier, password) => {
        // identifier peut être un email ou un pseudo
        const response = await api().post('/auth/login', { identifier, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        setPermissions(userData.permissions || {});
        return userData;
    };

    const loginWithPin = async (username, pin_code) => {
        // Connexion par pseudo + code PIN
        const response = await api().post('/auth/pin-login', { username, pin_code });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        setPermissions(userData.permissions || {});
        return userData;
    };

    const register = async (name, email, password) => {
        const response = await api().post('/auth/register', { name, email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        setPermissions(userData.permissions || {});
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setPermissions({});
    };

    // Refresh user data from server
    const refreshUser = async () => {
        if (!token) return null;
        try {
            const response = await api().get('/auth/me');
            setUser(response.data);
            setPermissions(response.data.permissions || {});
            return response.data;
        } catch (error) {
            console.error('Failed to refresh user:', error);
            return null;
        }
    };

    // Check if user has a specific permission
    const hasPermission = (permission) => {
        return permissions[permission] === true;
    };

    // Check if user has minimum role level
    const hasRoleLevel = (minLevel) => {
        const userRole = user?.role || '';
        const roleInfo = ROLES[userRole];
        return roleInfo ? roleInfo.level >= minLevel : false;
    };

    // Get current role info
    const getRoleInfo = () => {
        const userRole = user?.role || '';
        return ROLES[userRole] || { level: 0, label: userRole };
    };

    // Role checks
    const isDirecteur = user?.role === 'DIRECTEUR' || user?.role === 'ADMIN';
    const isDirecteurAdjoint = user?.role === 'DIRECTEUR_ADJOINT';
    const isChefCuisine = user?.role === 'CHEF_CUISINE';
    const isCuisinier = user?.role === 'CUISINIER';
    
    // Legacy compatibility
    const isAdmin = user?.role === 'ADMIN' || user?.role === 'admin';
    
    // Permission shortcuts
    const canViewPrices = hasPermission('view_purchase_price');
    const canViewMargins = hasPermission('view_margins');
    const canViewFinancial = hasPermission('view_financial_dashboard');
    const canManageUsers = hasPermission('manage_users');
    const canAccessAdmin = hasPermission('access_admin');
    
    // Loyalty Program Permission Shortcuts
    const canViewLoyalty = hasPermission('view_loyalty');
    const canScanLoyalty = hasPermission('scan_loyalty');
    const canValidateLoyaltyPurchase = hasPermission('validate_loyalty_purchase');
    const canAddLoyaltyPointsManual = hasPermission('add_loyalty_points_manual');
    const canOfferLoyaltyBonus = hasPermission('offer_loyalty_bonus');
    const canCorrectLoyaltyAccount = hasPermission('correct_loyalty_account');
    const canViewLoyaltyHistory = hasPermission('view_loyalty_history');
    const canManageLoyaltySettings = hasPermission('manage_loyalty_settings');

    const value = {
        user,
        token,
        loading,
        login,
        loginWithPin,
        register,
        logout,
        refreshUser,
        api,
        permissions,
        hasPermission,
        hasRoleLevel,
        getRoleInfo,
        // Role booleans
        isDirecteur,
        isDirecteurAdjoint,
        isChefCuisine,
        isCuisinier,
        isAdmin, // Legacy
        isAuthenticated: !!user,
        // Permission shortcuts
        canViewPrices,
        canViewMargins,
        canViewFinancial,
        canManageUsers,
        canAccessAdmin,
        // Loyalty permissions
        canViewLoyalty,
        canScanLoyalty,
        canValidateLoyaltyPurchase,
        canAddLoyaltyPointsManual,
        canOfferLoyaltyBonus,
        canCorrectLoyaltyAccount,
        canViewLoyaltyHistory,
        canManageLoyaltySettings,
        // Currency
        currency: 'CFA',
        formatCurrency: (amount) => {
            if (amount === null || amount === undefined) return '0 CFA';
            const formatted = new Intl.NumberFormat('fr-FR').format(Math.round(amount));
            return `${formatted} CFA`;
        }
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
